import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface Section {
  id: string
  label: string
}

interface DetailSectionNavProps {
  hasTrips: boolean
}

export function DetailSectionNav({ hasTrips }: DetailSectionNavProps) {
  // 'photos' is a virtual section: it scrolls back to the top gallery and is
  // active whenever no real section has been reached yet.
  const sections: Section[] = [
    { id: 'photos', label: 'Fotky' },
    { id: 'about', label: 'O místě' },
    { id: 'amenities', label: 'Vybavení' },
    { id: 'conditions', label: 'Podmínky' },
    { id: 'map', label: 'Lokalita' },
    ...(hasTrips ? [{ id: 'trips', label: 'Výlety' }] : []),
  ]

  const stickyRef = useRef<HTMLElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState<string | null>(null)
  // The nav takes no space in the page flow (h-0 wrapper) and fades in only
  // once scrolling pins it to its sticky position.
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        ticking = false
        const sticky = stickyRef.current
        const bar = barRef.current
        if (sticky && bar) {
          const stickyTop = parseFloat(getComputedStyle(sticky).top) || 0
          setStuck(sticky.getBoundingClientRect().top <= stickyTop + 1)
        }
        // The bar's bottom edge is exactly where sections disappear behind
        // it — works for both mobile and desktop sticky offsets.
        const offset = (bar?.getBoundingClientRect().bottom ?? 0) + 24
        let current: string | null = null
        for (const section of sections) {
          if (section.id === 'photos') continue
          const element = document.getElementById(section.id)
          if (element && element.getBoundingClientRect().top <= offset) {
            current = section.id
          }
        }
        setActive(current ?? 'photos')
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasTrips])

  return (
    <nav ref={stickyRef} className="sticky top-[calc(env(safe-area-inset-top)+94px)] z-30 h-0 lg:top-0">
      <div
        ref={barRef}
        className={cn(
          'border-b bg-white/95 backdrop-blur transition-opacity duration-200',
          stuck ? 'border-slate-100 opacity-100' : 'pointer-events-none border-transparent opacity-0',
        )}
      >
        <div className="flex gap-5 overflow-x-auto">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                if (section.id === 'photos') {
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                  return
                }
                document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' })
              }}
              className={cn(
                'border-b-2 py-2.5 text-sm font-medium whitespace-nowrap transition-colors',
                active === section.id
                  ? 'border-red text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-900',
              )}
            >
              {section.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  )
}
